import {
  GetNftListCategory,
  GetNftListFilter,
  GetNftListQueryAttributes,
} from '@share/interfaces';
import { InfoBox, SocialBox, SocialBoxContainer } from '@style/emotion';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { css } from '@emotion/react';
import { showPercentage } from '@util/helper';
import { useAPI, useHelper } from 'src/hooks';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { web3 } from '@state/web3';
import Multiselect from 'multiselect-react-dropdown';
import loadable from '@loadable/component';
import styled from '@emotion/styled';

const Footer = loadable(() => import('@com/footer/Footer'));
const Header = loadable(() => import('@com/header/Header'));
const Meta = loadable(() => import('@com/Meta'));
const NFTActivity = loadable(() => import('@com/collection/NFTActivity'));
const NFTList = loadable(() => import('@com/collection/NFTList'));
const SkeletonImage = loadable(() => import('@com/utils/SkeletonImage'));
const TopSection = loadable(() => import('@com/TopSection'));
const ShareBtn = loadable(() => import('@com/buttons/ShareBtn'));

const SortBox = styled.div`
  display: flex;
  margin-bottom: 3rem;
  margin-right: 2rem;

  @media (max-width: 612px) {
    width: 100%;
    display: flex;
    margin-right: 0;
    h3 {
      width: 14rem;
    }
    select {
      flex-grow: 1;
    }
  }
`;

const CollectionInfoBox = styled(InfoBox)`
  color: #fff;
  a {
    color: #fff;
  }
`;

const LogoContainer = styled.div`
  width: min(256px, 100%);
`;

function Collection(): JSX.Element {
  /* Define state / hook */
  const [sortBy, setSortBy] = useState<GetNftListFilter>('token_id');
  const [filter, setFilter] = useState<GetNftListCategory>('all');
  const [attributes, setAttributes] = useState<GetNftListQueryAttributes[]>();

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { _paramsChainId, collectionIdentifier } = useParams();
  const { t } = useTranslation();
  const { getChainName, addressExplorerBaseURL } = web3.useContainer();
  const { getUserIdentifier, showDescription, displayName, showAddressLink } =
    useHelper();

  const paramsChainId = Number(_paramsChainId);
  const { getCollectionDetails } = useAPI();
  const { data } = useQuery(
    [getCollectionDetails.name, collectionIdentifier, paramsChainId],
    () => getCollectionDetails(collectionIdentifier!, paramsChainId),
    {
      enabled: collectionIdentifier != null && Number.isInteger(paramsChainId),
      retry: 0,
    }
  );

  /* Custom Data Object for render or other usages */
  const tabIndexMap: Record<string, number> = {
    items: 0,
    activity: 1,
  };

  const filterList: { text: string; value: GetNftListFilter }[] = [
    { text: t('collection.name'), value: 'token_id' },
    { text: t('collection.priceAsc'), value: 'price_l_h' },
    { text: t('collection.priceDes'), value: 'price_h_l' },
    { text: t('collection.date'), value: 'create_date' },
  ];

  const categoryList: { text: string; value: GetNftListCategory }[] = [
    { text: t('collection.all'), value: 'all' },
    { text: t('collection.onSale'), value: 'on_sale' },
    { text: t('collection.onBid'), value: 'on_auctions' },
  ];

  /* Render control function / logic */
  const handleFiltering = (val: GetNftListQueryAttributes[]) => {
    setAttributes(val);
  };

  /* Data Change Listeners - useEffect/useMemo/useCallback */
  const PARAM_NAME = 'type';
  const selectedIndex = useMemo(() => {
    return tabIndexMap[String(searchParams.get(PARAM_NAME))] || 0;
  }, [location.search]);

  const attributeList = useMemo(() => {
    const result: GetNftListQueryAttributes[] = [];

    if (data?.attributes) {
      const keys = Object.keys(data?.attributes);
      for (let i = 0; i < keys?.length; i += 1) {
        const traitType = keys[i];
        const traitValue = data?.attributes[keys[i]];

        for (let x = 0; x < traitValue?.length; x += 1) {
          const value = traitValue?.[x];
          result.push({ category: traitType, value });
        }
      }
    }
    return result;
  }, [data?.attributes]);

  /* Styling */

  // const ViewSwither = styled.div`
  //   cursor: pointer;
  //   display: flex;
  //   justify-content: center;
  //   align-items: center;
  //   background-color: var(--primary-color7);
  //   border-radius: 0.4em;
  //   height: 48px;
  //   width: 48px;
  //   margin: 0.5rem 1.5rem 3rem auto;

  //   i {
  //     color: var(--primary-color4);
  //     font-size: 28px;
  //   }

  //   @media (max-width: 612px) {
  //     margin-left: 0;
  //   }
  // `;

  return (
    <>
      {/* Prevent undefined wording when loading API */}
      {data?.name && (
        <Meta title={`${process.env.REACT_APP_SHORT_TITLE} | ${data?.name}`} />
      )}
      <Header />
      <TopSection />
      <section className="tf-section authors">
        <div className="themesflat-container">
          <div className="flat-tabs tab-authors">
            <div className="author-profile flex-image">
              <LogoContainer>
                <SkeletonImage
                  alt="Collection Image"
                  ratio={1}
                  size={[512]}
                  src={data?.logo_image}
                />
              </LogoContainer>

              <div className="infor-profile">
                <h2 className="title w-full">{data?.name}</h2>
                <CollectionInfoBox>
                  {showDescription(data?.description) ||
                    t('general.noDescription')}
                </CollectionInfoBox>
                <CollectionInfoBox>
                  <span className="mr-3">{t('itemDetails.network')}</span>
                  {getChainName(paramsChainId)}
                </CollectionInfoBox>
                <CollectionInfoBox>
                  <span className="mr-3">{t('itemDetails.contractOwner')}</span>
                  {data?.owner?.uuid ? (
                    <Link to={`/profile/${getUserIdentifier(data?.owner)}`}>
                      {displayName(data?.owner?.name)}
                    </Link>
                  ) : (
                    <a
                      href={`${addressExplorerBaseURL(paramsChainId)}/${
                        data?.owner?.wallet?.wallet_address
                      }`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {displayName(data?.owner?.wallet?.wallet_address)}
                    </a>
                  )}
                </CollectionInfoBox>
                <CollectionInfoBox>
                  {data?.address && (
                    <div
                      className="d-flex"
                      css={css`
                        i {
                          color: #fff !important;
                        }
                      `}
                    >
                      <span className="mr-3">
                        {t('itemDetails.contractAddress')}
                      </span>
                      {showAddressLink(data.address, paramsChainId)}
                    </div>
                  )}
                </CollectionInfoBox>
                <CollectionInfoBox>
                  <span className="mr-3">
                    {t('itemDetails.artistRoyalties')}
                  </span>
                  {showPercentage(data?.royalty, 0)}
                </CollectionInfoBox>
                <CollectionInfoBox>
                  <span className="mr-3">{t('collection.totalNFT')}</span>
                  {data?.total_nft || 0}
                </CollectionInfoBox>
                <CollectionInfoBox>
                  <span className="mr-3">{t('collection.totalListed')}</span>
                  {data?.total_listed || 0}
                </CollectionInfoBox>
              </div>
              <SocialBoxContainer className="widget-social style-3">
                <SocialBox>
                  {data &&
                    Object.values(data?.social_links)?.map((item) => {
                      return (
                        item?.url && (
                          <li key={item?.name}>
                            <a
                              href={item?.url}
                              rel="noreferrer"
                              target="_blank"
                            >
                              <i className={item?.icon} />
                            </a>
                          </li>
                        )
                      );
                    })}
                  <li>
                    <ShareBtn className="ml-auto" />
                  </li>
                </SocialBox>
              </SocialBoxContainer>
            </div>
          </div>
        </div>
      </section>
      <section className="tf-explore tf-section">
        <div className="themesflat-container">
          <div className="row">
            <div className="col-12">
              <div className="flat-tabs themesflat-tabs w-full">
                <Tabs
                  selectedIndex={selectedIndex}
                  onSelect={(index) => {
                    navigate(
                      `${location.pathname}?${PARAM_NAME}=${
                        Object.keys(tabIndexMap)[index]
                      }`
                    );
                  }}
                >
                  <TabList className="center mb-5">
                    <Tab>{t('collection.items')}</Tab>
                    <Tab>{t('collection.activity')}</Tab>
                  </TabList>

                  <TabPanel>
                    <div
                      className="pl-3 style-2 d-flex flex-wrap pt-4"
                      id="sort-by"
                    >
                      <SortBox>
                        <h3 className="m-4">{t('collection.sortBy')}</h3>
                        <select
                          className="select"
                          value={sortBy}
                          onChange={(e) => {
                            setSortBy(e.target.value as GetNftListFilter);
                          }}
                        >
                          {filterList.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.text}
                            </option>
                          ))}
                        </select>
                      </SortBox>
                      <SortBox>
                        <h3 className="m-4">{t('collection.nftStatus')}</h3>
                        <select
                          className="select"
                          value={filter}
                          onChange={(e) => {
                            setFilter(e.target.value as GetNftListCategory);
                          }}
                        >
                          {categoryList.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.text}
                            </option>
                          ))}
                        </select>
                      </SortBox>
                      {/* <ViewSwither onClick={handleViewSwitch}>
                        <i className="fal fa-table" />
                      </ViewSwither> */}
                    </div>
                    <div className="pl-3 style-2 d-flex flex-wrap pt-4">
                      <SortBox>
                        <h3 className="m-4">{t('collection.filter')}</h3>
                        <Multiselect
                          showCheckbox
                          className="d-flex align-items-center h-100"
                          displayValue="value"
                          groupBy="category"
                          options={attributeList}
                          placeholder={t('collection.select')}
                          selectedValues={attributes}
                          onRemove={handleFiltering}
                          onSelect={handleFiltering}
                        />
                      </SortBox>
                    </div>
                    {data && (
                      <NFTList
                        attributes={attributes}
                        collectionAddress={data.address}
                        filter={filter}
                        sort={sortBy}
                      />
                    )}
                  </TabPanel>

                  <TabPanel className="min-h-50">
                    <NFTActivity contractAddress={data?.address} />
                  </TabPanel>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Collection;
